import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { global, useMenuStore } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { callApi } from "@shared/api/restApi";

const FALLBACK_ICON = "/images/icon_default.svg";

// 대시보드 열기 메뉴
const DASHBOARD_MENU_CD = "SN_SYS_DSH_REP_001";

const getMenuIcon = (cd_module?: string) => {
  const mod = String(cd_module || "").toLowerCase();
  return mod ? `/images/icon_${mod}.svg` : FALLBACK_ICON;
};

interface DashboardProps {
  onMenuClick?: (
    menuKey: string,
    menuTitle: string,
    cd_module: string,
    filePath: string,
    data: any,
    menuPath: string,
    nodeData?: any,
    figure?: any,
    originMenu?: any
  ) => void;
}

const buildPathMap = (menuItems: any[]) => {
  const map = new Map<string, string>();
  if (!Array.isArray(menuItems)) return map;
  for (const item of menuItems) {
    if (item.cd_menu_parent === "SN_REC" || item.cd_menu_parent === "SN_FAV") {
      continue;
    }
    if (item.fg_menu === "MGM") continue;
    if (Array.isArray(item.path_arr)) {
      const nm_path = item.path_arr
        .map((code: string) => {
          const m = menuItems.find((it) => it.cd_menu === code);
          return m ? m.nm_menu : "";
        })
        .join(" > ");
      map.set(item.cd_menu, nm_path);
    } else {
      map.set(item.cd_menu, "");
    }
  }
  return map;
};

const Dashboard: React.FC<DashboardProps> = ({ onMenuClick }) => {
  const { userInfo } = global();
  const { menuItems, setMenuItems } = useMenuStore();
  const [query, setQuery] = useState("");

  const userName =
    (userInfo?.fg_user === "2" ? userInfo?.nm_user : userInfo?.nm_emp) ||
    userInfo?.nm_user ||
    "";

  const pathMap = useMemo(() => buildPathMap(menuItems), [menuItems]);

  const moduleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (menuItems || []).forEach((it: any) => {
      if (it.cd_module && it.no_level == 1 && it.fg_menu === "MGM") {
        map.set(String(it.cd_module).toLowerCase(), it.nm_menu);
      }
    });
    return map;
  }, [menuItems]);

  const recentMenus = useMemo(
    () =>
      (menuItems || [])
        .filter(
          (m: any) => m.cd_menu_parent === "SN_REC" && m.fg_menu !== "MGM"
        )
        .slice(0, 12),
    [menuItems]
  );
  const favoriteMenus = useMemo(
    () =>
      (menuItems || [])
        .filter(
          (m: any) => m.cd_menu_parent === "SN_FAV" && m.fg_menu !== "MGM"
        )
        .slice(0, 12),
    [menuItems]
  );

  // 최근/즐겨찾기 사본이 아닌 원본 메뉴를 우선 사용
  const dashboardMenu = useMemo(() => {
    const matched = (menuItems || []).filter(
      (m: any) => m.cd_menu === DASHBOARD_MENU_CD && m.fg_menu !== "MGM"
    );
    return (
      matched.find(
        (m: any) =>
          m.cd_menu_parent !== "SN_REC" && m.cd_menu_parent !== "SN_FAV"
      ) || matched[0]
    );
  }, [menuItems]);

  const openLockRef = useRef<{ key: string; t: number }>({ key: "", t: 0 });

  const openMenu = (item: any) => {
    if (!onMenuClick || !item) return;
    const { cd_menu, nm_menu, cd_module, nm_file } = item;
    if (!cd_menu || !nm_menu || !cd_module || !nm_file) return;

    const now = Date.now();
    if (
      openLockRef.current.key === cd_menu &&
      now - openLockRef.current.t < 800
    ) {
      return; // 같은 메뉴의 연속 클릭 무시
    }
    openLockRef.current = { key: cd_menu, t: now };

    const nm_path = pathMap.get(cd_menu) ?? "";
    const filePath = `./${cd_module}/${nm_file}/${nm_file}`.toUpperCase();
    onMenuClick(cd_menu, nm_menu, cd_module, filePath, item, nm_path);
  };

  // 즐겨찾기 해제 — TabToolbar.onBookmark 의 삭제 분기와 동일
  const removeFavorite = async (item: any) => {
    const result = await callApi("delFav", {
      cd_firm: userInfo.cd_firm,
      cd_menu: item.cd_menu,
      cd_user: userInfo.cd_user,
    });
    if (!result.status) return;

    const { bookmark, setBookmark } = global.getState();
    const updated = { ...bookmark };
    delete updated[item.cd_menu];
    setBookmark(updated);

    setMenuItems(
      menuItems.filter(
        (m: any) =>
          m.cd_menu !== item.cd_menu || m.cd_menu_parent !== "SN_FAV"
      )
    );
  };

  const openDashboard = () => {
    if (!dashboardMenu) {
      showDialog("alter", "대시보드 메뉴 권한이 없습니다.");
      return;
    }
    openMenu(dashboardMenu);
  };

  const quickActions = [
    { label: "재고 현황", icon: <Inventory2OutlinedIcon fontSize="small" /> },
    { label: "납기 지연", icon: <ScheduleOutlinedIcon fontSize="small" /> },
    { label: "최근 주문", icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { label: "메뉴 찾기", icon: <SearchOutlinedIcon fontSize="small" /> },
  ];

  const CARD_BORDER = "1px solid #eceef4";

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100%",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #f7f9fd 0%, #f2f5fb 100%)",
        px: { xs: 3, md: 6 },
        pt: { xs: 3, md: 5 },
        pb: { xs: 2.5, md: 3.5 },
      }}
    >
      {/* 우측 배경 일러스트 */}
      <Box
        component="img"
        src="/images/main_bg.png"
        alt=""
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
        sx={{
          position: "absolute",
          top: { md: 8 },
          right: { md: 24 },
          width: { md: "min(46vw, 720px)" },
          maxWidth: "720px",
          height: "auto",
          pointerEvents: "none",
          userSelect: "none",
          display: { xs: "none", md: "block" },
          zIndex: 0,
        }}
      />

      {/* 좌상단 영역 */}
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 720, mb: 3 }}>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 38 },
            fontWeight: 700,
            color: "#1f2440",
            lineHeight: 1.25,
          }}
        >
          안녕하세요,{" "}
          <Box component="span" sx={{ color: "#3d5afe" }}>
            {userName ? `${userName}님` : "반갑습니다"}
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 2,
            fontSize: { xs: 15, md: 18 },
            color: "#7b8194",
          }}
        >
          오늘도 SmartNotes와 함께 업무를 시작해 보세요.
        </Typography>

        {/* AI 프롬프트 카드 */}
        <Box
          sx={{
            mt: 3,
            p: { xs: 2, md: 2.5 },
            background: "#ffffff",
            border: CARD_BORDER,
            borderRadius: "20px",
            boxShadow: "0 12px 30px rgba(79, 110, 220, 0.08)",
            display: "none",
            // display: "block",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component="input"
              value={query}
              placeholder="무엇을 도와드릴까요?"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              sx={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 18,
                color: "#2b3040",
                px: 1,
                "::placeholder": { color: "#a7adbd" },
              }}
            />
            <Box
              role="button"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "50%",
                cursor: "pointer",
                color: "#fff",
                background: "linear-gradient(135deg, #5b7bfb 0%, #7b5cff 100%)",
                boxShadow: "0 8px 18px rgba(91, 123, 251, 0.35)",
                transition: "transform 120ms ease",
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              <SendRoundedIcon fontSize="small" />
            </Box>
          </Box>

          {/* 빠른 실행 칩 */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            {quickActions.map((action) => (
              <Box
                key={action.label}
                onClick={() => setQuery(action.label)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.75,
                  py: 1,
                  borderRadius: "999px",
                  border: "1px solid #e5e9f2",
                  color: "#5a6273",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "#fbfcff",
                  "& svg": { color: "#5b7bfb" },
                  "&:hover": {
                    borderColor: "#c3d0f7",
                    background: "#f4f7ff",
                  },
                }}
              >
                {action.icon}
                {action.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 하단 그룹: 알림 바 + 카드 */}
      <Box sx={{ mt: "auto", width: "100%" }}>
        {/* 처리할 업무 알림 바 */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            // display: "none",
            display: "flex",
            alignItems: "center",
            gap: "clamp(16px, 8%, 120px)",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 355,
              maxWidth: 1000,
              // display: "flex",
              display: "none",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 1.75,
              borderRadius: "16px",
              background: "#eef1fb",
              border: "1px solid #e3e7f6",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "#ffffff",
                color: "#5b7bfb",
                flexShrink: 0,
              }}
            >
              <NotificationsNoneRoundedIcon fontSize="small" />
            </Box>

            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: "#20263c",
                whiteSpace: "nowrap",
              }}
            >
              처리할 업무{" "}
              <Box component="span" sx={{ color: "#3d5afe" }}>
                0건
              </Box>
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                color: "#5a6273",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {["구매 요청 승인", "품질 검사 등록", "출고 처리 대기"].map(
                (label, idx) => (
                  <React.Fragment key={label}>
                    {idx > 0 && (
                      <Box
                        component="span"
                        sx={{ color: "#c5cbdc", userSelect: "none" }}
                      >
                        |
                      </Box>
                    )}
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      {label} 0건
                    </Box>
                  </React.Fragment>
                )
              )}
            </Box>

            <ChevronRightRoundedIcon
              sx={{
                ml: "auto",
                color: "#9aa2b6",
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
          </Box>

          {/* 대시보드 열기 버튼 */}
          <Box
            role="button"
            onClick={openDashboard}
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 2.25,
              py: 1.25,
              borderRadius: "10px",
              border: "2px solid #9db2f0",
              background: "transparent",
              color: "#3d5afe",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "rgba(61, 90, 254, 0.06)",
                borderColor: "#7f97ec",
              },
            }}
          >
            <InsertChartOutlinedRoundedIcon fontSize="small" />
            대시보드 열기
          </Box>
        </Box>

        {/* 하단 카드 영역 */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            mt: { xs: 2.5, md: 3 },
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2.5,
          }}
        >
          <MenuCard
            title="최근 사용 메뉴"
            emptyText="최근 사용한 메뉴가 없습니다."
            items={recentMenus}
            onItemClick={openMenu}
            cardBorder={CARD_BORDER}
            moduleNameMap={moduleNameMap}
          />
          <MenuCard
            title="즐겨찾기"
            emptyText="즐겨찾기한 메뉴가 없습니다."
            items={favoriteMenus}
            onItemClick={openMenu}
            cardBorder={CARD_BORDER}
            moduleNameMap={moduleNameMap}
            showFavoriteBadge
            onFavoriteClick={removeFavorite}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface MenuCardProps {
  title: string;
  emptyText: string;
  items: any[];
  onItemClick: (item: any) => void;
  cardBorder: string;
  moduleNameMap: Map<string, string>;
  showFavoriteBadge?: boolean;
  onFavoriteClick?: (item: any) => void;
}

const TILE_MIN_WIDTH = 92;
const TILE_GAP = 12;

const MenuCard: React.FC<MenuCardProps> = ({
  title,
  emptyText,
  items,
  onItemClick,
  cardBorder,
  moduleNameMap,
  showFavoriteBadge = false,
  onFavoriteClick,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(items.length || 1);

  // 카드 폭이 바뀔 때마다 한 줄에 들어갈 타일 개수를 다시 계산
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const measure = () => {
      const width = el.clientWidth;
      if (!width) return;
      const fit = Math.floor((width + TILE_GAP) / (TILE_MIN_WIDTH + TILE_GAP));
      setColumns(Math.max(1, fit));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visibleItems = items.slice(0, columns);

  return (
    <Box
      sx={{
        background: "#ffffff",
        border: cardBorder,
        borderRadius: "18px",
        p: { xs: 2, md: 2.5 },
        boxShadow: "0 8px 24px rgba(79, 110, 220, 0.06)",
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#20263c" }}>
        {title}
      </Typography>
      <Box
        ref={gridRef}
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns:
            items.length === 0 ? "1fr" : `repeat(${columns}, minmax(0, 1fr))`,
          gap: 1.5,
        }}
      >
        {items.length === 0 ? (
          <Typography
            sx={{
              gridColumn: "1 / -1",
              color: "#a7adbd",
              fontSize: 14,
              py: 4.5,
              textAlign: "center",
            }}
          >
            {emptyText}
          </Typography>
        ) : (
          visibleItems.map((item: any) => (
            <Box
              key={item.cd_menu}
              onClick={() => onItemClick(item)}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 3.5,
                px: 1,
                borderRadius: "14px",
                border: "1px solid #eef1f7",
                background: "#fbfcff",
                cursor: "pointer",
                transition: "all 120ms ease",
                "&:hover": {
                  borderColor: "#c3d0f7",
                  background: "#f4f7ff",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* 즐겨찾기 별 아이콘 */}
              {showFavoriteBadge && (
                <StarRoundedIcon
                  titleAccess="즐겨찾기 해제"
                  onClick={(e) => {
                    e.stopPropagation(); // 타일 클릭(메뉴 열기) 막기
                    onFavoriteClick?.(item);
                  }}
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    fontSize: 20,
                    color: "#4F74FF",
                    cursor: "pointer",
                    "&:hover": { color: "#c3cbe0" },
                  }}
                />
              )}
              <Box
                component="img"
                src={getMenuIcon(item.cd_module)}
                alt=""
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_ICON;
                }}
                sx={{
                  width: 35,
                  height: 35,
                  objectFit: "contain",
                  flexShrink: 0,
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
              {(() => {
                const moduleName =
                  moduleNameMap.get(
                    String(item.cd_module || "").toLowerCase()
                  ) || "";
                return (
                  <Box sx={{ width: "100%", textAlign: "center" }}>
                    {moduleName && (
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#2b3040",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                        title={moduleName}
                      >
                        {moduleName}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: moduleName ? 12 : 13,
                        fontWeight: moduleName ? 500 : 700,
                        color: moduleName ? "#8a90a2" : "#2b3040",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                      title={item.nm_menu}
                    >
                      {item.nm_menu}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
